import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FaCoins,
  FaDiscord,
  FaDice,
  FaGem,
  FaRedo,
  FaSignOutAlt,
  FaTrophy,
  FaBriefcase,
  FaCalendarDay,
  FaHistory,
} from 'react-icons/fa';
import Navbar from '../../components/Navbar/Navbar';
import discordService from '../../services/discordService';
import {
  fetchEconomyWallet,
  claimDaily,
  claimWork,
  playCoinflip,
  playDice,
  playSlots,
  TX_LABELS,
} from '../../services/economyService';
import './Economia.css';

const GAMES = [
  {
    id: 'coinflip',
    title: 'Cara ou Coroa',
    blurb: 'Escolha um lado. Acerto dobra a aposta.',
    payout: '×2',
  },
  {
    id: 'dice',
    title: 'Dados',
    blurb: 'Role contra a casa. Maior vence; empate devolve.',
    payout: '×2',
  },
  {
    id: 'slots',
    title: 'Slots',
    blurb: 'Três iguais ×4 · 💎 ×8 · Dois iguais ×1,5',
    payout: 'até ×8',
  },
];

const BET_PRESETS = [10, 50, 100, 250, 500, 1000];
const SLOT_SPIN = ['🍒', '🍋', '🔔', '⭐', '💎', '7️⃣'];

const discordAvatarUrl = (user, size = 128) => {
  if (!user?.id) return null;
  if (user.avatar) {
    return `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png?size=${size}`;
  }
  const index = Number(String(user.id).slice(-2)) % 6;
  return `https://cdn.discordapp.com/embed/avatars/${index}.png`;
};

const formatDuration = (ms) => {
  const totalSec = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const formatNc = (amount) => {
  const n = Number(amount) || 0;
  return `NC ${n.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatTxTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const Economia = () => {
  const [user, setUser] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [cooldowns, setCooldowns] = useState(null);
  const [statement, setStatement] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [now, setNow] = useState(Date.now());

  const [activeGame, setActiveGame] = useState('coinflip');
  const [bet, setBet] = useState(50);
  const [busy, setBusy] = useState('');
  const [lastResult, setLastResult] = useState(null);

  // Visual state for animations
  const [coinFace, setCoinFace] = useState('cara');
  const [coinSpinning, setCoinSpinning] = useState(false);
  const [dicePlayer, setDicePlayer] = useState(1);
  const [diceHouse, setDiceHouse] = useState(1);
  const [diceRolling, setDiceRolling] = useState(false);
  const [slotReels, setSlotReels] = useState(['⭐', '🍋', '💎']);
  const [slotSpinning, setSlotSpinning] = useState(false);

  const showToast = useCallback((text) => {
    setToast(text);
    window.setTimeout(() => setToast(''), 2800);
  }, []);

  const applyWalletPayload = useCallback((data) => {
    if (data.wallet) setWallet(data.wallet);
    if (data.cooldowns) setCooldowns(data.cooldowns);
    if (data.statement) setStatement(data.statement);
    if (data.ranking) setRanking(data.ranking);
    if (data.limits) setLimits(data.limits);
    if (typeof data.balance === 'number') {
      setWallet((prev) =>
        prev
          ? {
              ...prev,
              balance: data.balance,
              balanceFormatted: data.balanceFormatted || formatNc(data.balance),
            }
          : prev
      );
    }
  }, []);

  const loadWallet = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchEconomyWallet();
      applyWalletPayload(data);
    } catch (err) {
      setError(err.message || 'Não foi possível carregar o Nexus Pay.');
      if (err.code === 'NO_TOKEN' || err.code === 'UNAUTHORIZED') {
        setWallet(null);
      }
    } finally {
      setLoading(false);
    }
  }, [applyWalletPayload]);

  useEffect(() => {
    const auth = discordService.getPlayerAuth();
    setUser(auth);
    if (auth) loadWallet();
  }, [loadWallet]);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const remaining = useCallback(
    (key) => {
      const cd = cooldowns?.[key];
      if (!cd?.expiresAt) return 0;
      return Math.max(0, new Date(cd.expiresAt).getTime() - now);
    },
    [cooldowns, now]
  );

  const connectDiscord = () => {
    window.location.href = discordService.getAuthUrl('economia');
  };

  const logout = () => {
    discordService.clearPlayerAuth();
    setUser(null);
    setWallet(null);
    setStatement([]);
    setRanking([]);
    setError('');
    window.dispatchEvent(new Event('nexus:auth-changed'));
  };

  const wait = (ms) => new Promise((r) => window.setTimeout(r, ms));

  const runClaim = async (kind) => {
    if (busy) return;
    setBusy(kind);
    setError('');
    try {
      const data = kind === 'daily' ? await claimDaily() : await claimWork();
      applyWalletPayload(data);
      showToast(
        kind === 'daily'
          ? `Daily: +${data.amountFormatted || formatNc(data.amount)}`
          : `Trabalho: +${data.amountFormatted || formatNc(data.amount)}`
      );
      await loadWallet();
    } catch (err) {
      setError(err.message || 'Falha ao resgatar.');
      if (err.data?.cooldowns) setCooldowns(err.data.cooldowns);
    } finally {
      setBusy('');
    }
  };

  const animateCoinflip = async (resultSide) => {
    setCoinSpinning(true);
    await wait(1400);
    setCoinFace(resultSide);
    setCoinSpinning(false);
  };

  const animateDice = async (player, house) => {
    setDiceRolling(true);
    const start = Date.now();
    while (Date.now() - start < 1100) {
      setDicePlayer(1 + Math.floor(Math.random() * 6));
      setDiceHouse(1 + Math.floor(Math.random() * 6));
      await wait(70);
    }
    setDicePlayer(player);
    setDiceHouse(house);
    setDiceRolling(false);
  };

  const animateSlots = async (finalReels) => {
    setSlotSpinning(true);
    const start = Date.now();
    while (Date.now() - start < 1600) {
      setSlotReels([
        SLOT_SPIN[Math.floor(Math.random() * SLOT_SPIN.length)],
        SLOT_SPIN[Math.floor(Math.random() * SLOT_SPIN.length)],
        SLOT_SPIN[Math.floor(Math.random() * SLOT_SPIN.length)],
      ]);
      await wait(80);
    }
    setSlotReels(finalReels);
    setSlotSpinning(false);
  };

  const playGame = async (extra = {}) => {
    if (busy) return;
    const amount = Number(bet);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('Informe uma aposta válida.');
      return;
    }

    setBusy(activeGame);
    setError('');
    setLastResult(null);

    try {
      let data;
      if (activeGame === 'coinflip') {
        data = await playCoinflip(amount, extra.choice || 'cara');
        await animateCoinflip(data.result);
      } else if (activeGame === 'dice') {
        data = await playDice(amount);
        await animateDice(data.player, data.house);
      } else {
        data = await playSlots(amount);
        await animateSlots(data.reels || ['🍋', '🔔', '⭐']);
      }

      applyWalletPayload(data);
      setLastResult({ game: activeGame, ...data });
      showToast(
        data.won
          ? `Vitória! +${data.profitFormatted || formatNc(data.profit)}`
          : data.tie
            ? 'Empate — aposta devolvida'
            : `Derrota · ${data.profitFormatted || formatNc(data.profit)}`
      );
      await loadWallet();
    } catch (err) {
      setError(err.message || 'Falha ao jogar.');
      if (err.data?.cooldowns) setCooldowns(err.data.cooldowns);
      if (typeof err.data?.balance === 'number') {
        setWallet((prev) =>
          prev
            ? {
                ...prev,
                balance: err.data.balance,
                balanceFormatted: formatNc(err.data.balance),
              }
            : prev
        );
      }
    } finally {
      setBusy('');
    }
  };

  const minBet = limits?.gameMinBet ?? 10;
  const maxBet = limits?.gameMaxBet ?? 5000;
  const balance = wallet?.balance ?? 0;

  const gameCd = remaining('game');
  const dailyCd = remaining('daily');
  const workCd = remaining('work');

  const resultTone = useMemo(() => {
    if (!lastResult) return '';
    if (lastResult.won) return 'win';
    if (lastResult.tie) return 'tie';
    return 'lose';
  }, [lastResult]);

  return (
    <div className="economia">
      <Navbar />
      {toast && <div className="eco-toast">{toast}</div>}

      <header className="eco-hero">
        <div className="eco-hero__glow" aria-hidden="true" />
        <div className="eco-hero__grid" aria-hidden="true" />
        <div className="eco-hero__orbit" aria-hidden="true" />
        <div className="eco-wrap eco-hero__content">
          <p className="eco-kicker">Nexus Pay · Arena</p>
          <h1 className="eco-brand">NEXUS PAY</h1>
          <p className="eco-lead">
            Economia do bot com visual de arena — daily, trabalho e minigames no
            mesmo lugar.
          </p>
        </div>
      </header>

      <main className="eco-main eco-wrap">
        {!user ? (
          <section className="eco-gate">
            <div className="eco-gate__panel">
              <FaDiscord className="eco-gate__icon" aria-hidden="true" />
              <h2>Entre com Discord</h2>
              <p>
                Conecte a mesma conta usada no servidor para jogar com sua
                carteira Nexus Coins.
              </p>
              <button type="button" className="eco-btn eco-btn--primary" onClick={connectDiscord}>
                <FaDiscord aria-hidden="true" /> Conectar Discord
              </button>
            </div>
          </section>
        ) : (
          <>
            <section className="eco-toolbar">
              <div className="eco-toolbar__user">
                <img
                  className="eco-toolbar__avatar"
                  src={discordAvatarUrl(user, 80)}
                  alt=""
                  width={48}
                  height={48}
                />
                <div>
                  <strong>{user.global_name || user.username}</strong>
                  <span>@{user.username}</span>
                </div>
              </div>
              <div className="eco-toolbar__actions">
                <button
                  type="button"
                  className="eco-btn eco-btn--ghost"
                  onClick={loadWallet}
                  disabled={loading}
                >
                  <FaRedo aria-hidden="true" /> Atualizar
                </button>
                <button type="button" className="eco-btn eco-btn--ghost" onClick={logout}>
                  <FaSignOutAlt aria-hidden="true" /> Sair
                </button>
              </div>
            </section>

            {error && <div className="eco-alert">{error}</div>}

            <section className="eco-wallet">
              <article className="eco-balance eco-balance--main">
                <div className="eco-balance__label">
                  <FaCoins aria-hidden="true" /> Nexus Coins
                </div>
                <p className="eco-balance__value">
                  {loading && !wallet ? '…' : wallet?.balanceFormatted || formatNc(0)}
                </p>
                <p className="eco-balance__hint">Carteira sincronizada com o bot</p>
              </article>

              <article className="eco-balance">
                <div className="eco-balance__label">
                  <FaGem aria-hidden="true" /> Nexus Cristais
                </div>
                <p className="eco-balance__value eco-balance__value--sm">
                  {wallet?.linked
                    ? wallet.crystalsFormatted || '⟡ 0,00'
                    : 'Não vinculado'}
                </p>
                <p className="eco-balance__hint">
                  {wallet?.nickname
                    ? `${wallet.nickname}${wallet.online ? ' · online' : ''}`
                    : 'Vincule no Discord com /vincular'}
                </p>
              </article>

              <div className="eco-claims">
                <button
                  type="button"
                  className="eco-claim"
                  disabled={busy === 'daily' || dailyCd > 0}
                  onClick={() => runClaim('daily')}
                >
                  <FaCalendarDay aria-hidden="true" />
                  <span>
                    <strong>Daily</strong>
                    <small>
                      {dailyCd > 0
                        ? `Disponível em ${formatDuration(dailyCd)}`
                        : `+${formatNc(limits?.dailyAmount ?? 250)}`}
                    </small>
                  </span>
                </button>
                <button
                  type="button"
                  className="eco-claim"
                  disabled={busy === 'work' || workCd > 0}
                  onClick={() => runClaim('work')}
                >
                  <FaBriefcase aria-hidden="true" />
                  <span>
                    <strong>Trabalhar</strong>
                    <small>
                      {workCd > 0
                        ? `Disponível em ${formatDuration(workCd)}`
                        : `${formatNc(limits?.workMin ?? 40)} – ${formatNc(limits?.workMax ?? 120)}`}
                    </small>
                  </span>
                </button>
              </div>
            </section>

            <section className="eco-arena">
              <div className="eco-arena__head">
                <h2>Arena</h2>
                <p>Apostas de {formatNc(minBet)} a {formatNc(maxBet)} · cooldown 4s</p>
              </div>

              <div className="eco-games" role="tablist">
                {GAMES.map((game) => (
                  <button
                    key={game.id}
                    type="button"
                    role="tab"
                    aria-selected={activeGame === game.id}
                    className={`eco-game-tab${activeGame === game.id ? ' is-active' : ''}`}
                    onClick={() => {
                      setActiveGame(game.id);
                      setLastResult(null);
                    }}
                  >
                    <span className="eco-game-tab__title">{game.title}</span>
                    <span className="eco-game-tab__pay">{game.payout}</span>
                    <span className="eco-game-tab__blurb">{game.blurb}</span>
                  </button>
                ))}
              </div>

              <div className="eco-stage">
                <div className={`eco-stage__canvas eco-stage__canvas--${activeGame}`}>
                  {activeGame === 'coinflip' && (
                    <div className={`eco-coin${coinSpinning ? ' is-spinning' : ''}`} data-face={coinFace}>
                      <div className="eco-coin__inner">
                        <div className="eco-coin__face eco-coin__face--cara">🟡</div>
                        <div className="eco-coin__face eco-coin__face--coroa">⚪</div>
                      </div>
                    </div>
                  )}

                  {activeGame === 'dice' && (
                    <div className={`eco-dice-row${diceRolling ? ' is-rolling' : ''}`}>
                      <div className="eco-die">
                        <span>Você</span>
                        <strong><FaDice aria-hidden="true" /> {dicePlayer}</strong>
                      </div>
                      <div className="eco-die eco-die--vs">VS</div>
                      <div className="eco-die">
                        <span>Casa</span>
                        <strong><FaDice aria-hidden="true" /> {diceHouse}</strong>
                      </div>
                    </div>
                  )}

                  {activeGame === 'slots' && (
                    <div className={`eco-slots${slotSpinning ? ' is-spinning' : ''}`}>
                      {slotReels.map((sym, i) => (
                        <div key={i} className="eco-slot" style={{ animationDelay: `${i * 0.08}s` }}>
                          <span>{sym}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {lastResult && lastResult.game === activeGame && (
                  <div className={`eco-result eco-result--${resultTone}`}>
                    {lastResult.won && 'Vitória'}
                    {!lastResult.won && lastResult.tie && 'Empate'}
                    {!lastResult.won && !lastResult.tie && 'Derrota'}
                    {' · '}
                    {lastResult.profitFormatted || formatNc(lastResult.profit)}
                    {lastResult.multiplier
                      ? ` · ×${lastResult.multiplier}`
                      : ''}
                  </div>
                )}

                <div className="eco-bet">
                  <label htmlFor="eco-bet-input">Aposta</label>
                  <div className="eco-bet__row">
                    <input
                      id="eco-bet-input"
                      type="number"
                      min={minBet}
                      max={Math.min(maxBet, Math.max(minBet, balance))}
                      step="1"
                      value={bet}
                      onChange={(e) => setBet(e.target.value)}
                    />
                    <button
                      type="button"
                      className="eco-btn eco-btn--ghost"
                      onClick={() =>
                        setBet(Math.min(maxBet, Math.max(minBet, Math.floor(balance))))
                      }
                    >
                      Max
                    </button>
                  </div>
                  <div className="eco-bet__presets">
                    {BET_PRESETS.filter((v) => v >= minBet && v <= maxBet).map((v) => (
                      <button
                        key={v}
                        type="button"
                        className={Number(bet) === v ? 'is-active' : ''}
                        onClick={() => setBet(v)}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="eco-play">
                  {activeGame === 'coinflip' ? (
                    <>
                      <button
                        type="button"
                        className="eco-btn eco-btn--primary eco-btn--wide"
                        disabled={Boolean(busy) || gameCd > 0}
                        onClick={() => playGame({ choice: 'cara' })}
                      >
                        🟡 Cara
                      </button>
                      <button
                        type="button"
                        className="eco-btn eco-btn--secondary eco-btn--wide"
                        disabled={Boolean(busy) || gameCd > 0}
                        onClick={() => playGame({ choice: 'coroa' })}
                      >
                        ⚪ Coroa
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="eco-btn eco-btn--primary eco-btn--wide"
                      disabled={Boolean(busy) || gameCd > 0}
                      onClick={() => playGame()}
                    >
                      {busy === activeGame
                        ? 'Jogando…'
                        : gameCd > 0
                          ? `Aguarde ${formatDuration(gameCd)}`
                          : activeGame === 'dice'
                            ? 'Rolar dados'
                            : 'Girar slots'}
                    </button>
                  )}
                </div>
              </div>
            </section>

            <section className="eco-panels">
              <article className="eco-panel">
                <header>
                  <FaTrophy aria-hidden="true" />
                  <h3>Ranking</h3>
                </header>
                <ol className="eco-rank">
                  {ranking.length === 0 && (
                    <li className="eco-empty">Ninguém no top ainda.</li>
                  )}
                  {ranking.map((row) => (
                    <li key={row.discordId} className={row.isYou ? 'is-you' : ''}>
                      <span className="eco-rank__pos">#{row.rank}</span>
                      <span className="eco-rank__id">
                        {row.isYou ? 'Você' : `…${String(row.discordId).slice(-4)}`}
                      </span>
                      <span className="eco-rank__bal">{row.balanceFormatted}</span>
                    </li>
                  ))}
                </ol>
              </article>

              <article className="eco-panel">
                <header>
                  <FaHistory aria-hidden="true" />
                  <h3>Extrato</h3>
                </header>
                <ul className="eco-tx">
                  {statement.length === 0 && (
                    <li className="eco-empty">Sem movimentações recentes.</li>
                  )}
                  {statement.map((tx) => (
                    <li key={tx.id}>
                      <div>
                        <strong>{TX_LABELS[tx.type] || tx.type}</strong>
                        <small>{formatTxTime(tx.createdAt)}</small>
                      </div>
                      <span className={tx.amount >= 0 ? 'is-pos' : 'is-neg'}>
                        {tx.amount >= 0 ? '+' : ''}
                        {formatNc(tx.amount)}
                      </span>
                    </li>
                  ))}
                </ul>
              </article>
            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default Economia;
